'use client';

import { useState, useEffect, useRef } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { watchtowerPosts } from '../../../lib/api/watchtower';
import { WatchtowerPostWithAuthor } from '../../../lib/types/watchtower';
import { createClient } from '../../../lib/supabase/client';
import { tickerToSlug } from '@/lib/ticker-url';
import { STOCK_LINK_CLASS } from '@/lib/stock-link-style';
import BrailleSpinner from '../../components/BrailleSpinner';
import { HUB_PANEL_SURFACE } from '@/lib/hub/hub-panel-surface';
import { FONT_BASTION } from '@/lib/bastion-button';
import { watchtowerPostPath, watchtowerSeoTitle } from '@/lib/watchtower/slug';

export default function PostContent({ initialPost = null }: { initialPost?: WatchtowerPostWithAuthor | null }) {
  const params = useParams();
  const router = useRouter();
  const postId = params.postId as string;

  // Published posts arrive server-rendered so crawlers see the body; drafts
  // (admin-only) still load client-side.
  const [post, setPost] = useState<WatchtowerPostWithAuthor | null>(initialPost);
  const [loading, setLoading] = useState(!initialPost);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSeoTitle, setEditSeoTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!postId) return;
    if (initialPost) {
      fetch('/api/auth/check-admin').then(r => r.json())
        .then(adminData => setIsAdmin(!!adminData.isAdmin))
        .catch(() => {});
      return;
    }
    setLoading(true);

    const postPromise = watchtowerPosts.getById(postId);
    const adminPromise = fetch('/api/auth/check-admin').then(r => r.json()).catch(() => ({ isAdmin: false }));

    Promise.all([postPromise, adminPromise])
      .then(([data, adminData]) => {
        setPost(data);
        setIsAdmin(!!adminData.isAdmin);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load post');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [postId, initialPost]);

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };

  useEffect(() => { autoResize(titleRef.current); }, [editTitle, editing]);
  useEffect(() => { autoResize(contentRef.current); }, [editContent, editing]);

  const startEditing = () => {
    if (!post) return;
    setEditTitle(post.title || '');
    setEditContent(post.content || '');
    setEditSeoTitle(post.seo_title || '');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditTitle('');
    setEditContent('');
  };

  const saveEdit = async () => {
    if (!post || saving) return;
    setSaving(true);
    try {
      await watchtowerPosts.update({
        id: post.id,
        title: editTitle.trim() || editContent.trim().slice(0, 100),
        content: editContent.trim() || '',
        seo_title: editSeoTitle.trim() || null,
        image_urls: post.image_urls?.length ? post.image_urls : undefined,
        tags: post.tags?.length ? post.tags : undefined,
      });
      setPost(prev => prev ? {
        ...prev,
        title: editTitle.trim() || editContent.trim().slice(0, 100),
        content: editContent.trim() || '',
        seo_title: editSeoTitle.trim() || null,
      } : null);
      setEditing(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    // Fixed timezone so the server-rendered date matches hydration.
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  };

  const handleShare = async () => {
    if (!post) return;
    const url = `${window.location.origin}${watchtowerPostPath(post)}`;
    try {
      if (navigator.share) await navigator.share({ title: post.title || 'Watchtower', url });
      else { await navigator.clipboard.writeText(url); }
    } catch {}
  };

  const renderContent = (content: string) => {
    if (!content) return null;
    const paragraphs = content.split(/\n\n+/);

    const processText = (text: string) => {
      const regex = /(\*\*(.+?)\*\*)|(\$(?!\d+[BMKT]?\b)[A-Z0-9]{1,10}(?::[A-Z]{2,4})?)|(https?:\/\/[^\s<]+[^\s<.,;:!?)\]"'])|([a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}(?:\/[^\s<]*[^\s<.,;:!?)\]"'])?)/g;
      const parts: React.ReactNode[] = [];
      let lastIdx = 0;
      let match;
      let key = 0;
      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIdx) parts.push(text.slice(lastIdx, match.index));
        if (match[1]) {
          parts.push(<strong key={key++} style={{ color: '#E5E7EB', fontWeight: 600 }}>{match[2]}</strong>);
        } else if (match[3]) {
          const ticker = match[3].slice(1);
          const full = ticker.includes(':') ? ticker : `${ticker}:USA`;
          parts.push(
            <a key={key++} href={`/stock/${tickerToSlug(full)}`} className={STOCK_LINK_CLASS} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: '#d4a54a', fontWeight: 600 }}>{match[3]}</a>
          );
        } else if (match[4] || match[5]) {
          const url = match[4] || match[5];
          const href = url.startsWith('http') ? url : `https://${url}`;
          parts.push(
            <a key={key++} href={href} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: '#3bd576', textDecoration: 'none', borderBottom: '1px solid rgba(59,213,118,0.3)' }}>{url}</a>
          );
        }
        lastIdx = match.index + match[0].length;
      }
      if (lastIdx < text.length) parts.push(text.slice(lastIdx));
      return parts;
    };

    return paragraphs.map((para, i) => {
      const lines = para.split(/\n/);
      return (
        <p key={i} style={{
          marginBottom: i < paragraphs.length - 1 ? 22 : 0,
          fontSize: 15.5,
          lineHeight: 1.55,
          color: 'rgba(255,255,255,0.72)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
          letterSpacing: '-0.003em',
          wordWrap: 'break-word',
        }}>
          {lines.map((line, j) => (<span key={j}>{j > 0 && <br />}{processText(line)}</span>))}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <BrailleSpinner />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', marginBottom: 16 }}>{error || 'Post not found'}</p>
          <a href="/watchtower" style={{ color: '#3bd576', textDecoration: 'none', fontSize: 13 }}>← Back to Watchtower</a>
        </div>
      </div>
    );
  }

  const images = (() => {
    let imgs: string[] = [];
    if (post.image_urls?.length) {
      imgs = [...new Set(post.image_urls)];
      if (post.image_url && !imgs.includes(post.image_url)) imgs.push(post.image_url);
    } else if (post.image_url) {
      imgs = [post.image_url];
    }
    return imgs;
  })();

  return (
    <div style={{ minHeight: '100vh', color: '#fff' }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px 60px' }}>

        {/* Back */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
          <a href="/watchtower"
            onClick={(e) => { if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); router.back(); } }}
            style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, textDecoration: 'none', transition: 'color 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#3bd576'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
          >← Watchtower</a>

          {isAdmin && !editing && (
            <button onClick={startEditing}
              style={{ background: 'none', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', padding: '4px 12px', borderRadius: 6, fontSize: 12, transition: 'all 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
            >Edit</button>
          )}

          {editing && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={cancelEditing}
                style={{ background: 'none', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '4px 12px', borderRadius: 6, fontSize: 12 }}
              >Cancel</button>
              <button onClick={saveEdit} disabled={saving}
                style={{ background: saving ? 'rgba(59,213,118,0.2)' : '#3bd576', border: 'none', color: saving ? 'rgba(255,255,255,0.3)' : '#000', cursor: saving ? 'not-allowed' : 'pointer', padding: '4px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}
              >{saving ? 'Saving...' : 'Save'}</button>
            </div>
          )}
        </div>

        {/* Note card */}
        <article style={{
          ...HUB_PANEL_SURFACE,
          padding: '32px 36px 28px',
        }}>
          {/* Date & tags */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.28)', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 400 }}>
              {formatDate(post.published_at)}
            </div>
            {post.tags && post.tags.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {post.tags.map((tag, i) => (
                  <span key={i} style={{ fontSize: 12, color: 'rgba(59,213,118,0.55)', fontWeight: 500 }}>#{tag}</span>
                ))}
              </div>
            )}
          </div>

          {editing ? (
            <>
              {/* Editable title */}
              <textarea
                ref={titleRef}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Title"
                style={{
                  width: '100%', background: 'transparent', border: 'none', outline: 'none', resize: 'none', overflow: 'hidden',
                  fontSize: 28, fontWeight: 700, color: '#E5E7EB', letterSpacing: '-0.025em', lineHeight: 1.3,
                  margin: '0 0 20px', padding: 0,
                  fontFamily: 'var(--font-inter-tight), -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                }}
              />

              {/* Search title (Google / social). Empty = trimmed post title. */}
              <input
                value={editSeoTitle}
                onChange={(e) => setEditSeoTitle(e.target.value)}
                placeholder={`SEO title (optional) — ${watchtowerSeoTitle({ title: editTitle })}`}
                maxLength={70}
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 6, outline: 'none', padding: '6px 10px', margin: '0 0 6px',
                  fontSize: 13, color: 'rgba(255,255,255,0.8)', fontFamily: FONT_BASTION,
                }}
              />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: '0 0 16px', fontFamily: FONT_BASTION }}>
                {(editSeoTitle.trim() || watchtowerSeoTitle({ title: editTitle })).length}/60 · shown in Google results
              </div>

              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 0 20px' }} />

              {/* Editable content */}
              <textarea
                ref={contentRef}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Write something..."
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); saveEdit(); } }}
                style={{
                  width: '100%', background: 'transparent', border: 'none', outline: 'none', resize: 'none', overflow: 'hidden',
                  fontSize: 15.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.72)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
                  letterSpacing: '-0.003em', padding: 0, minHeight: 200,
                }}
              />
            </>
          ) : (
            <>
              {/* Title */}
              <h1 style={{
                fontSize: 28, fontWeight: 700, color: '#E5E7EB', letterSpacing: '-0.025em', lineHeight: 1.3,
                margin: '0 0 20px',
                fontFamily: 'var(--font-inter-tight), -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              }}>
                {post.title}
              </h1>

              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 0 20px' }} />

              {/* Body */}
              {post.content && (
                <div style={{ marginBottom: images.length > 0 ? 24 : 0 }}>
                  {renderContent(post.content)}
                </div>
              )}
            </>
          )}

          {/* Images */}
          {images.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {images.map((url, i) => (
                <div key={i} style={{
                  borderRadius: 10, overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.06)', background: '#0a0a0a',
                  marginBottom: i < images.length - 1 ? 8 : 0, cursor: 'pointer',
                }} onClick={() => setSelectedImage(url)}>
                  <img src={url} alt={post.image_alt || ''}
                    style={{ width: '100%', height: 'auto', maxHeight: '65vh', objectFit: 'contain', display: 'block' }}
                    onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                </div>
              ))}
            </div>
          )}
        </article>

        {/* Share footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 4px' }}>
          <button onClick={handleShare}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', cursor: 'pointer', padding: '4px 0', transition: 'color 0.15s', fontSize: 12 }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#3bd576'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.2)'; }}
          >
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            Share
          </button>
        </div>
      </div>

      {/* Image lightbox */}
      {selectedImage && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setSelectedImage(null)}>
          <div style={{ position: 'relative', maxWidth: '95vw', maxHeight: '95vh' }} onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="" style={{ maxWidth: '100%', maxHeight: '95vh', objectFit: 'contain', borderRadius: 8 }} />
            <button onClick={() => setSelectedImage(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}
